import type { Estate, EstateFilters, FilterOptions, BookingPayload, BookingResponse } from '../types';

const API_BASE_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8000';

class ApiClient {
  private initData: string = '';

  setInitData(initData: string) {
    this.initData = initData;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add Telegram initData for authentication
    if (this.initData && this.initData.length > 0) {
      headers['Authorization'] = `tma ${this.initData}`;
      // Debug logging
      console.log('Sending Authorization header with initData:', {
        length: this.initData.length,
        preview: this.initData.substring(0, 50) + '...',
        headerValue: `tma ${this.initData.substring(0, 30)}...`,
      });
    } else {
      console.error('No initData available for request!', {
        hasInitData: !!this.initData,
        initDataLength: this.initData?.length || 0,
        endpoint,
      });
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      const errorMessage = error.message || error.detail || `HTTP ${response.status}`;
      
      // Log auth errors for debugging
      if (response.status === 401) {
        console.error('Authentication failed:', {
          hasInitData: !!this.initData,
          initDataLength: this.initData?.length || 0,
          error: errorMessage,
        });
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // Estates
  async getEstates(filters?: EstateFilters): Promise<Estate[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.country) params.set('country', filters.country);
      if (filters.minBedrooms) params.set('min_bedrooms', filters.minBedrooms.toString());
      if (filters.maxBedrooms) params.set('max_bedrooms', filters.maxBedrooms.toString());
      if (filters.activities?.length) params.set('activities', filters.activities.join(','));
      if (filters.amenities?.length) params.set('amenities', filters.amenities.join(','));
      if (filters.search) params.set('search', filters.search);
    }

    const queryString = params.toString();
    const endpoint = `/api/estates${queryString ? `?${queryString}` : ''}`;
    
    return this.request<Estate[]>(endpoint);
  }

  async getEstate(id: string): Promise<Estate> {
    return this.request<Estate>(`/api/estates/${id}`);
  }

  async getFilterOptions(): Promise<FilterOptions> {
    return this.request<FilterOptions>('/api/filters');
  }

  // Bookings
  async submitBooking(payload: BookingPayload): Promise<BookingResponse> {
    return this.request<BookingResponse>('/api/bookings', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/api/health');
  }
}

export const api = new ApiClient();

